import { EmptyCertificates } from './EmptyCertificates';
import { CertificatesLoadError } from './CertificatesLoadError';

import React, { useEffect } from 'react';

import { Checkbox, Divider, alpha, Collapse } from '@mui/material';
import {
  CheckSquare,
  LayoutList,
  ArrowRightLeft,
  UserCog,
  CircleHelp,
  LogOut as LogOutIcon,
  BellIcon,
} from 'lucide-react';
import { Footer } from 'components/Footer';
import { getScrollPosition, clearScrollPosition } from 'utils/scrollRestore';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { FILTER_PANEL_CONTENT_SX } from 'components/UI/filterPanelSx';
import { ActionMenu } from 'components/UI/ActionMenu';
import type { ActionMenuItem } from 'components/UI/ActionMenu';
import { Loader } from 'components/UI/Loader';
import { PageContainer } from 'components/UI/PageContainer';
import { CONTENT_INNER_PAD, PAGE_GUTTER } from 'constants/layout';
import { LogOut } from 'components/pages/profile/LogOut';
import { CertificatesFilter } from 'components/pages/certificates/CertificatesFilter';
import CertificatesGrid from './CertificatesGrid';
import CertificatesGridSkeleton from './CertificatesGridSkeleton';
import { PullToRefresh } from 'components/UI/PullToRefresh';
import { SortDrawer } from 'components/UI/SortDrawer';
import type { SortOption } from 'components/UI/SortDrawer';

/*
 * The certificates list's sortable fields. `last_activity` is a DATE and was
 * offering "A - Z" / "Z - A" like every other surface except Scans; declaring the
 * type here is what fixes it.
 */
const CERTIFICATES_SORT_OPTIONS: SortOption[] = [
  /*
   * `name` reads the PRODUCT TITLE (see getCertificateValue), so it is labelled
   * "Title" — which is also what the certificate page calls it, in the page's own
   * heading. "Certificate name" implied a separate field that does not exist.
   */
  { key: 'name', label: 'Title', type: 'string' },
  { key: 'status', label: 'Status', type: 'string' },
  { key: 'owned_since', label: 'Ownership date', type: 'date' },
  { key: 'last_activity', label: 'Last activity date', type: 'dateTime' },
];
import SortMenuDesktop from './SortMenuDesktop';
import SortConfigPopover from './SortConfigPopover';
import HeaderBar from './HeaderBar';
import SelectionToolbar from './SelectionToolbar';

import { ALLOWED_TABS } from 'constants/certificateConstants';

import { useCertificatesPageState } from './useCertificatesPageState';
import { useArrivingTransfers } from './useArrivingTransfers';
import { useMyPendingSales } from 'hooks/api/pendingOwnership';
import type { PendingSale } from 'types';
import SwipeableViews from 'react-swipeable-views';
import type { CerteficateTab, Sort } from 'types';
import { FormProvider, useForm } from 'react-hook-form';

import {
  useTheme,
  Tabs,
  Tab,
  Stack,
  Typography,
  Box,
  Button,
  useMediaQuery,
} from '@mui/material';
import { certificatesTabsSx } from './tabsSx';
import { MENU_ROW_LABEL_SX, menuRowToneSx } from 'components/UI/menuRowTone';

import { LinkAccounts } from '../signIn/LinkAccounts';
import AddIcon from '@mui/icons-material/Add';
import { DisplayedDataPopover } from './DisplayedDataPopover';
import { Button as UIButton } from '@repo/ui';
import { fieldSurfaceSx } from 'components/UI/fieldMetrics';
import { Plus as PlusIcon } from 'lucide-react';
import { surfaceRadius } from 'settings/brandRadius';

export type CheckedItems = {
  type: string;
  label: string;
  count: number;
  value: string;
};

type Filters = {
  limit: number;
  offset: number;
  tab: CerteficateTab;
  status: string | null;
  type: string | null;
  sort: Sort | null;
  sort_by: string | null;
  query: string | null;
  certificateIds: string | null;
  activityFrom?: string;
  activityTo?: string;
};

export const CerteficatesPage = () => {
  const methods = useForm();
  const { direction, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('md'));

  const {
    // State
    filters,
    tab,
    linkAccounts,
    isFilterChanged,
    isLogOutOpen,
    filteredItems,
    allCertificates,
    selectedCertificateIds,
    anchorEl,
    sortMenuAnchor,
    sortConfigAnchor,
    moreOptionsAnchor,
    displayedDataAnchor,
    sortButtonRef,
    displayedData,
    layout,

    // UI State
    uiState,
    sortState,

    // Data
    certificates,
    visibleCertificates,
    tabsSummaryList,
    isEmpty,
    total,
    loadedPages,
    loadMore,
    isHasMorePages,
    isFetching,
    isLoading,
    isRefetching,
    isPlaceholderData,
    isError,
    error,

    // Selection
    selectedIds,
    isSelected,
    toggleSelected,
    selectAll,
    clearSelection,

    // Computed
    filterBadgeCount,
    sortBadgeCount,
    showSortClear,

    // Setters
    setFilters,
    setTab,
    setLinkAccounts,
    setMobileAddSort,
    setIsFilterChanged,
    setIsLogOutOpen,
    setFilteredItems,
    setAllCertificates,
    setSelectedCertificateIds,
    setAnchorEl,
    setSortMenuAnchor,
    setSortConfigAnchor,
    setMoreOptionsAnchor,
    setDisplayedDataAnchor,
    setOpenSort,
    setOpenFilter,
    setOpenMore,
    setOpenSelect,
    toggleSelect,
    setOpenDisplayedData,
    setMobileSortStep,
    setMobileSortField,
    setMobileSortDir,
    setAdditionalSorts,

    // Handlers
    onFiltersChange,
    onFiltersClear,
    onSortChange,
    onSearchChange,
    handleTabsChange,
    handleChangeIndex,
    handleFolderClick,
    handleMenuItemClick,
    performActionBasedOnMenuItem,
    handleSortClick,
    handleSortClear,
    handleDisplayedDataChange,
    handleLayoutChange,
    onStatusesChange,
    onOwnershipChange,

    // Actions
    refetch,
  } = useCertificatesPageState();

  /*
   * VERA-304 / VRG-337 — animate an arriving gift in.
   *
   * ORDERING IS NOT DONE HERE. The hoist that used to live in this hook could
   * only reorder the page the client already had, and the list pages server-side
   * at 10 — so a gift arriving for an owner with more than a page could sit on
   * page 3 and never surface. The pin is now a leading `ORDER BY` key in the API
   * query (VERA-304 A4), which is the only layer that can decide what page 1
   * CONTAINS. The rows below are rendered in exactly the order the server sent
   * them; a second, client-side sort on top would be a competing opinion about
   * the same question and would show up as rows jumping on the 20s poll.
   *
   * The `isDefaultSort` guard moved server-side with the pin: the API applies it
   * only when the request names no sort key, which is the same rule.
   *
   * The key is built from the same `filters` + `tab` that form the react-query
   * key, so the hook can tell a background poll (same key -> a genuine arrival)
   * from a filter/tab/page change (new key -> a whole new set, none of which
   * "arrived"). See the hook for why that distinction is load-bearing.
   */
  const { arrivingIds } = useArrivingTransfers({
    certificates: visibleCertificates,
    queryKey: JSON.stringify([filters, tab]),
    // Only a RESOLVED result for this key may baseline or produce an arrival —
    // see the prop's note for the two ways this goes wrong without it.
    settled: !isLoading && !isPlaceholderData,
  });

  /**
   * VRG-455 — items this buyer has BOUGHT but not yet claimed.
   *
   * A separate query rather than a field on the certificates response, because
   * they are not certificates: no certificate exists for them yet, which is the
   * entire premise. Folding them into that payload would have meant a shape
   * where half the "certificates" have no id, no status and no tag.
   *
   * NOT filtered, sorted, searched or paged with the certificates list. Every
   * control this page offers keys on a certificate field these rows do not have
   * (status, last activity, certificate id), so applying them would silently
   * hide an item the buyer owns behind a filter that cannot describe it. They
   * are a short, action-bearing set, and they stay whole.
   */
  const { data: pendingSalesData } = useMyPendingSales();
  const pendingSales = pendingSalesData?.items ?? [];


  /**
   * Restore the list's scroll position when returning from a certificate.
   *
   * Deferred until the cards actually EXIST: the restore runs against the
   * document's scroll height, and immediately after mount that height is the
   * empty page, so scrolling to 1400px silently clamps to 0 and the position is
   * lost. Keyed on the rendered count so it fires on the first render that has
   * something to scroll, and cleared once used so a later fresh visit starts at
   * the top rather than somewhere the user has no memory of.
   *
   * With infinite scroll this depends on the loaded-page count coming back too,
   * and it does: `useCertificatesPageState` restores it from `getLoadedPages`
   * BEFORE the first query, so the one request that resolves here already
   * carries every row the saved offset refers to. Restore the scroll without
   * the page count and you scroll a one-page document to a four-page offset,
   * the browser clamps it, and nothing appears to happen.
   */
  /*
    THE INFINITE-SCROLL SENTINEL.

    `rootMargin: '400px'` so the next page is already in flight by the time the
    end of the list reaches the fold — a loader the reader actually watches is a
    loader that arrived too late.

    Every guard here is load-bearing. `isFetching` rather than `isRefetching`,
    because a load-more changes the query key, which makes it a FIRST fetch of
    that key and leaves `isRefetching` false — the sentinel would re-fire under
    its own request and skip a page. The observer is torn down and rebuilt
    whenever those inputs change, so it never closes over a stale `loadMore`.

    It co-exists with the button it wraps rather than replacing it: see the
    sentinel's own note at the render site for why both.
  */
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !isHasMorePages || isFetching) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) loadMore();
      },
      { rootMargin: '400px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isHasMorePages, isFetching, loadMore]);

  const restoredScrollRef = React.useRef(false);
  React.useLayoutEffect(() => {
    if (restoredScrollRef.current) return;
    const saved = getScrollPosition('/certificates');
    if (saved === undefined || !visibleCertificates?.length) return;
    restoredScrollRef.current = true;
    window.scrollTo({ top: saved, behavior: 'auto' });
    clearScrollPosition('/certificates');
  }, [visibleCertificates?.length]);

  return (
    <FormProvider {...methods}>
      <Stack
        className='w-full mx-auto'
        sx={{ flex: 1, minHeight: '100vh', background: theme => theme.palette.background.default }}
      >
        {/*
          THE SHARED CONTAINER. No `maxWidth` — there is one content width now and
          `PageContainer` owns it (see `CONTENT_MAX_WIDTH` in `constants/layout`).

          This page used to pass `INDEX_MAX_WIDTH` and a 48px `INDEX_GUTTER`, on the
          argument that a card grid earns more width than a record you read. The
          width half was already true by accident — both are 1280 — but the GUTTER
          half made this page the NARROWEST column in the app at every viewport below
          1520, which is every laptop: measured 944 here against 992 everywhere else.
          The grid gained 40px a side when the gutter came down to `PAGE_GUTTER`.

          ⚠️ `gutter` still gets an override, and it is arithmetic, not a width
          choice: `PAGE_GUTTER` (24) is delivered in three parts here, because
          `HeaderBar` pads itself 16px and the grid wrapper is `p-4`. The container
          owes only the remaining 8. `gutterMobile={0}` likewise reproduces `xs: 0` —
          on a phone those two children supply the whole gutter themselves.

          ⚠️ COUPLED: `HeaderBar`'s search field derives its cap from the same gutter,
          as the literal `48px` in its `calc`. If the gutter changes here it must
          change there in the same edit, or the field stops landing on the first
          card's right edge (it was 66.67px out on 2026-08-23 for exactly this).

          One thing DID go: `max-w-[1500px]`, a fifth width and a dead one. The
          layout's `<main>` already caps at 1280, so a 1500px bound could never bind
          at any viewport — it read as the page's cap while doing nothing, which is
          worse than either having one or not having one.
        */}
        <PageContainer
          gutter={PAGE_GUTTER - CONTENT_INNER_PAD}
          gutterMobile={0}
          sx={{ flex: '1 1 auto' }}
        >
          <Stack sx={{ flex: '1 1 auto' }}>
            <HeaderBar
              isMobile={isMobile}
              // "Certificates" on a phone, measured rather than chosen: at 20px
              // "My Certificates" is ~170px and the four controls ~200px, which
              // exceeds the 343px content width and wrapped them onto their own
              // row — the exact thing putting them beside the title was meant to
              // avoid. The shorter word also stops repeating the bottom bar's
              // own "Certificates" label two lines below it.
              title={isMobile ? 'Certificates' : 'My Certificates'}
              count={isLoading ? undefined : total}
              onSearchChange={onSearchChange}
              onFilterClick={() => setOpenFilter(true)}
              onFilterClear={onFiltersClear}
              filterBadgeCount={filterBadgeCount}
              sortButtonRef={sortButtonRef}
              onSortClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSortClick(e, isMobile)}
              sortBadgeCount={sortBadgeCount}
              // Just page state. The `!isMobile &&` that used to guard this is
              // gone: suppressing the clear affordance below `md` is
              // `SortControl`'s job, in CSS, alongside the `display` it has to
              // agree with — and doing it here fixed one of four callers while
              // gating on `useMediaQuery`, which does not re-evaluate on a live
              // resize in this app.
              showSortClear={showSortClear}
              onSortClear={handleSortClear}
              // Clear on OPEN, not on close. The bar should always open empty, and
              // doing it here is unconditional — hanging it off the collapse's
              // `onExited` made it depend on a transition callback firing, so a
              // reduced-motion or mid-flight unmount would leave the selection set
              // with nothing on screen showing it.
              selectOpen={uiState.openSelect}
              onSelectClick={() => {
                // TOGGLE. The control that opens the selection bar is the obvious
                // thing to press to get rid of it; pressing it again did nothing,
                // leaving the bar's own X as the only way out — a second target for
                // something the first one should already do.
                //
                // Clearing on every press, not only on open: when this press is the
                // one that CLOSES the bar, the selection is going away with it
                // anyway, so the two cases want the same thing.
                clearSelection();
                toggleSelect();
              }}
              onColumnsClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                setDisplayedDataAnchor(e.currentTarget);
                setOpenDisplayedData(true);
              }}
              belowRow={
                // Collapse, not a conditional render: appearing instantly makes the
                // list below jump by the bar's full height, which reads as a glitch
                // rather than as a mode turning on. Growing into place ties the bar
                // to the Select button that produced it.
                <Collapse in={uiState.openSelect} timeout={220} unmountOnExit>
                  <SelectionToolbar
                    certificates={certificates}
                    selectedIds={selectedIds}
                    onSelectAll={selectAll}
                    onClearSelection={clearSelection}
                    onClose={() => setOpenSelect(false)}
                    onDeselectIds={(ids: string[]) => ids.forEach(id => toggleSelected(id))}
                    /* Was `onTransfer={() => { /* TODO *\/ }}` — the bar's one
                       action, for a flow this app does not have, wired to a
                       handler that silently did nothing. The bar now offers the
                       same set a single certificate does and owns its own
                       drawers; what it needs from the page is the list refresh
                       after a bulk status change, which only the page can do. */
                    onActionComplete={() => refetch()}
                  />
                </Collapse>
              }
            />
            {/* VERA-304: pending-transfer banners are NOT rendered here.

                They were briefly pinned above the list so an arriving gift could
                not be missed. Seen with four pending gifts that is a wall of
                ~100px cards that pushes every actual certificate below the fold —
                the list stops being a list. One banner was arguable; N is not,
                and N is not rare (a sender clearing out several items produces it
                immediately).

                The state itself is not lost: it already lives in the per-
                certificate state block (StatusNotification's "Transfer in" card),
                which is also where the user can act on it.

                DISCOVERY is now handled by ORDERING rather than by banners — see
                `useArrivingTransfers` above. Hoisting the arriving certificate's
                own card to the top costs no extra height (it is a row that was
                going to render anyway, just in a different place) and so does not
                reintroduce the wall this note rejected. The cross-surface badge on
                the Activity tab is still the right home for "you have N things
                waiting" and remains VRG-337's scope. */}
            {!isMobile && (
              <SortMenuDesktop
                anchorEl={sortMenuAnchor}
                open={Boolean(sortMenuAnchor)}
                onClose={() => setSortMenuAnchor(null)}
                onPickField={(field, anchor) => {
                  setMobileSortField(field);
                  onSortChange(field, 'ASC');
                  setSortMenuAnchor(null);
                  // Hand off to the configure popover on the same button rather
                  // than dead-ending — see `SortMenuDesktop`'s "The pick HANDS
                  // OFF". The sort is already applied; this is what SHOWS it.
                  setSortConfigAnchor(anchor);
                }}
              />
            )}
            {!isMobile && (
              <SortConfigPopover
                anchorEl={sortConfigAnchor}
                open={Boolean(sortConfigAnchor)}
                onClose={() => setSortConfigAnchor(null)}
                primaryField={(filters.sort_by as string) ?? sortState.mobileSortField ?? 'name'}
                primaryDirection={((filters.sort ?? sortState.mobileSortDir) as any) ?? 'ASC'}
                additionalSorts={sortState.additionalSorts as any}
                disabledFields={[
                  ...(filters.sort_by ? [filters.sort_by as string] : []),
                  ...sortState.additionalSorts.map(s => s.field),
                ]}
                onPrimaryFieldChange={field => {
                  setMobileSortField(field);
                  onSortChange(field, (filters.sort ?? sortState.mobileSortDir) as any);
                }}
                onPrimaryDirectionChange={direction =>
                  onSortChange(
                    (filters.sort_by as string) ?? sortState.mobileSortField,
                    direction as any
                  )
                }
                onClearAll={() => {
                  onSortChange(null, null);
                  setAdditionalSorts([]);
                  setSortConfigAnchor(null);
                }}
                onChangeAdditional={(index, next) => {
                  const arr = [...sortState.additionalSorts];
                  arr[index] = next as any;
                  setAdditionalSorts(arr);
                }}
                onRemoveAdditional={index =>
                  setAdditionalSorts(sortState.additionalSorts.filter((_, i) => i !== index))
                }
                onAddAdditional={() => {
                  const total = (filters.sort_by ? 1 : 0) + sortState.additionalSorts.length;
                  if (total >= 3) return;
                  const used = [
                    ...(filters.sort_by ? [filters.sort_by as string] : []),
                    ...sortState.additionalSorts.map(s => s.field),
                  ];
                  const available = ['name', 'status', 'last_activity'].filter(
                    f => !used.includes(f)
                  );
                  const next = available[0] || 'name';
                  setAdditionalSorts([
                    ...sortState.additionalSorts,
                    { field: next, direction: 'ASC' },
                  ]);
                }}
              />
            )}

            <div className='p-4'>
              {/* VERA-304: `isLoading` ONLY — deliberately NOT `|| isRefetching`.
                  This epic added a 10s foreground poll to `useGetCertificates`, and
                  `isRefetching` is true on every one of those background refetches.
                  With it in this condition the whole grid unmounted behind a skeleton
                  and remounted every ~10 seconds, which destroyed any UI state living
                  inside it: reported live as the Gift drawer "flashing closed before I
                  can send" while typing a recipient (the drawer's open flag is local
                  state in `CertificatesMenu`, rendered per row inside this grid).

                  Before the poll this was nearly invisible — `isRefetching` only went
                  true on a manual refetch or a window-focus refetch. The poll turned a
                  rare flicker into a 10-second metronome, so the poll is what made an
                  existing latent bug load-bearing.

                  §19 already specifies this: the skeleton is for the FIRST load
                  (`isLoading` / no cached data); on a background refetch with stale
                  data already on screen, keep the data visible. */}
              {isLoading ? (
                /* `layout` is passed so the placeholder occupies the SAME
                   columns the cards will — the toggle changes the phone column
                   count, and a skeleton that ignored it would shift on every
                   first load in grid mode. */
                <CertificatesGridSkeleton layout={layout} />
              ) : isError ? (
                // VERA-837 fix: a 401/403 (or any load failure) must never be
                // indistinguishable from a genuine empty account — checked
                // BEFORE isEmpty, which would otherwise be true here too
                // (certificates stays undefined on a failed fetch).
                <CertificatesLoadError
                  message={error?.message}
                  status={(error as (Error & { status?: number }) | null)?.status}
                  onRetry={() => refetch()}
                />
              ) : isEmpty && pendingSales.length === 0 ? (
                /* VRG-455: an account with a pending purchase and no
                   certificates yet is NOT empty. Rendering "no certificates"
                   over a row the buyer can act on would be the page denying
                   something it is about to show. */
                <EmptyCertificates />
              ) : (
                /* VERA-304: pull down at the top of the list to refetch. Wraps only the
                   populated grid on purpose — pulling on a skeleton, an error state or
                   an empty account has nothing to refresh, and the error state already
                   carries its own Retry. This gesture is what let the poll relax from
                   10s to 20s (see `useGetCertificates`). */
                <PullToRefresh onRefresh={() => refetch()}>
                  <CertificatesGrid
                    layout={layout}
                    /* Rendered in the SERVER's order, unmodified. Any incoming
                       in-flight transfer is already first — pinned by the API's
                       ORDER BY, so it reaches page 1 however many certificates
                       this account holds (VERA-304 A4). */
                    certificates={visibleCertificates}
                    arrivingIds={arrivingIds}
                    onRefetch={() => refetch()}
                    selectMode={uiState.openSelect}
                    isSelected={id => isSelected(id)}
                    onToggleSelected={id => toggleSelected(id)}
                    displayedData={displayedData}
                    /* The swap transition, driven by placeholder state so a
                       20s background poll never triggers it. See the prop. */
                    pending={isPlaceholderData}
                    pendingSales={pendingSales}
                  />
                </PullToRefresh>
              )}
            </div>

            {/*
              THE END OF THE LIST — a sentinel that loads the next page when it
              comes into view, with an explicit button beside it.

              Both, not one. The observer is the infinite scroll; the button is
              what makes the list reachable without a pointer, and it is also
              the honest fallback for every case an IntersectionObserver does
              not fire (a hidden tab, a browser that throttles it, the dev
              preview pane). A list whose only way forward is a scroll event is
              a list that silently ends for anyone it does not reach.

              Replaced a numbered pager. The pager's one advantage over infinite
              scroll is that it preserves your place — and this one did not: its
              page lived in `useState` with no URL or storage behind it, so
              opening a certificate from page 4 and coming back landed on page
              1, while the SCROLL position was restored. It lost your place in
              the exact way a pager exists to prevent. See `saveLoadedPages`.
            */}
            {!isEmpty && isHasMorePages && (
              <Stack
                ref={loadMoreRef}
                alignItems='center'
                sx={{ padding: '20px 0 24px' }}
              >
                <Button
                  variant='text'
                  onClick={loadMore}
                  disabled={isFetching}
                  sx={theme => ({
                    ...fieldSurfaceSx(theme),
                    height: 48,
                    px: 3,
                    color: 'text.primary',
                  })}
                >
                  {isFetching ? 'Loading…' : 'Load more'}
                </Button>
              </Stack>
            )}
            {/* NOT wrapped in `open && (...)`. `handleOpen` below already controls
                it, and the conditional additionally UNMOUNTED the drawer the instant
                the flag went false — so MUI's exit Slide had nothing left to animate
                and the panel vanished rather than sliding away. The wrapper was pure
                redundancy that cost the close transition. Children still unmount on
                close (MUI Modal's `keepMounted` defaults false), so per-open state
                resets exactly as before. */}
              <DrawerPopup
                title='Filters'
                anchor={isMobile ? 'bottom' : 'right'}
                /*
                 * NOT fullScreen on desktop any more. `fullScreen` forces the Paper to
                 * `height: 100vh; overflow: hidden` inside DrawerPopup, and the desktop
                 * `paperSx` below then walked it back to `calc(100% - 32px)` — either way a
                 * VIEWPORT-height panel holding three short controls, which measured 768px
                 * tall with 391px of dead air between the Status pills and the footer.
                 *
                 * With it false, `resolvedPaperSx` passes `paperSx` through untouched, so the
                 * panel sizes to its CONTENT and MUI's own `.MuiDrawer-paper` overflow-y:auto
                 * takes over once the content outgrows the cap (the certificate picker's list
                 * is the case that gets there). It also makes DrawerPopup's header sticky,
                 * which is what you want the moment the body can scroll.
                 */
                fullScreen={false}
                /* Desktop only — makes DrawerPopup's content box FILL the panel so
                   CertificatesFilter's own `mt: 'auto'` footer has free space to push
                   into. Without it the box sized to its content, `height: 100%` on the
                   child resolved to `auto`, and Reset/Apply landed mid-panel with the
                   rest of the viewport empty below them. See `FILTER_PANEL_CONTENT_SX`. */
                contentSx={isMobile ? undefined : FILTER_PANEL_CONTENT_SX}
                /*
                 * NO 'Cancel' — the header X, which is what DrawerPopup draws when no
                 * right action is given (and what the desktop panel already showed).
                 *
                 * "Cancel" promised a revert, and under auto-apply there is nothing
                 * left to revert: every change has already committed by the time you
                 * reach the header. A control that says Cancel and dismisses without
                 * undoing anything is worse than no label at all, because it is the
                 * one word a user would trust to get their old filters back. Reset is
                 * now the only way back, and it clears to EMPTY rather than to the
                 * state you arrived in — that loss is real and is the accepted cost of
                 * auto-apply, so the UI must not keep advertising the old promise.
                 *
                 * The old handler's `setSelectedCertificateIds(prev => prev)` went with
                 * it: an identity setState, i.e. a no-op that read as a restore.
                 */
                paperSx={
                  isMobile
                    ? { maxHeight: '90vh' }
                    : {
                        width: 550,
                        maxWidth: '80vw',
                        /* 16px inset on all three exposed sides, so the panel FLOATS off the
                           window edge the way the platform's filter drawer does (its Paper
                           carries `margin: 4` = 16px on the 4px scale; `2` is the same 16px
                           on this app's 8px scale — the factor differs, the pixels match). */
                        mr: 2,
                        my: 2,
                        /* FULL viewport height, less the two 16px insets — the same shape
                           the PLATFORM's filter drawer takes (its scroll body is pinned by
                           `minHeight: vhMinus(172)` AND `maxHeight: vhMinus(172)`, i.e. it
                           fills regardless of how many filters it holds).

                           This was briefly `height: 'auto'` on the reasoning that three
                           controls do not need a viewport of panel and the dead space below
                           them read as empty. That was a misreading of the request, and it
                           was the wrong call anyway: a side panel is CHROME, and chrome that
                           changes size with its contents reads as unstable — it jumps as
                           filters are added or a section expands. The platform's fills for
                           that reason, not because it happens to hold more rows.

                           `height` must be STATED: MUI's `.MuiDrawer-paper` base sets
                           `height: 100%`, so the inset has to be subtracted explicitly. */
                        height: 'calc(100% - 32px)',
                        /* Was a literal 12px, matching the platform drawer's `borderRadius: 3`
                           (the theme's raw default 4px read as a square-ish slab once the panel
                           is inset far enough from the edge for the corners to be visible).
                           VRG-562: same 12px at the default token, tenant-driven above it. */
                        borderRadius: surfaceRadius,
                      }
                }
                child={
                  <CertificatesFilter
                    certificates={allCertificates}
                    isFilterChanged={isFilterChanged}
                    filteredItems={filteredItems}
                    onFilterSubmit={onFiltersChange}
                    mobile={isMobile}
                    onCertificateFilterChange={setSelectedCertificateIds}
                    initialSelectedCertificateIds={selectedCertificateIds}
                    initialActivityFrom={(filters as any).activityFrom ?? null}
                    initialActivityTo={(filters as any).activityTo ?? null}
                    statuses={(filters as any).statuses ?? []}
                    onStatusesChange={onStatusesChange}
                    ownership={(filters as any).ownership ?? 'current'}
                    onOwnershipChange={onOwnershipChange}
                  />
                }
                handleOpen={uiState.openFilter}
                handleClose={() => setOpenFilter(false)}
              />
            {isMobile && (
              <SortDrawer
                open={uiState.openSort}
                onClose={() => setOpenSort(false)}
                step={sortState.mobileSortStep}
                setStep={setMobileSortStep}
                options={CERTIFICATES_SORT_OPTIONS}
                primaryField={sortState.mobileSortField}
                primaryDirection={sortState.mobileSortDir}
                additionalSorts={sortState.additionalSorts}
                onPrimaryFieldChange={setMobileSortField}
                onPrimaryDirectionChange={setMobileSortDir}
                onClearAll={() => {
                  onSortChange(null, null);
                  setAdditionalSorts([]);
                  setMobileSortField('name');
                  setMobileSortDir('ASC');
                }}
                /* Uses the value the sheet hands over, NOT `sortState` — that
                   state is set in the same tick and is still the old field here. */
                onApply={primary => onSortChange(primary.field, primary.direction as Sort)}
                // ONE commit callback, fired on Apply. Previously three granular
                // ones that each wrote straight to page state, so the list
                // re-sorted underneath the still-open sheet.
                onAdditionalChange={next => setAdditionalSorts(next)}
              />
            )}
            {/* NOT wrapped in `open && (...)`. `handleOpen` below already controls
                it, and the conditional additionally UNMOUNTED the drawer the instant
                the flag went false — so MUI's exit Slide had nothing left to animate
                and the panel vanished rather than sliding away. The wrapper was pure
                redundancy that cost the close transition. Children still unmount on
                close (MUI Modal's `keepMounted` defaults false), so per-open state
                resets exactly as before. */}
              <DrawerPopup
                title=''
                hideHeader
                child={
                  <Stack spacing={1}>
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing={1.5}
                      // The app's one menu-row tone (`menuRowTone.ts`) — see the note
                      // on the row below about this block being a duplicate.
                      sx={(theme: any) => ({
                        cursor: 'pointer',
                        px: 2,
                        py: 1.5,
                        // Absolute px, NOT the tenant token: menu chrome is
                        // deliberately excluded from the brand radius (VRG-562;
                        // see `packages/ui/theme/menuChrome.ts` — "a menu row's
                        // corner would silently track a setting that has nothing
                        // to do with menus").
                        borderRadius: '8px',
                        ...menuRowToneSx(theme),
                      })}
                      onClick={() => {
                        setOpenMore(false);
                        setOpenSelect(true);
                      }}
                    >
                      <CheckSquare size={20} />
                      <Typography sx={{ fontSize: 16, ...MENU_ROW_LABEL_SX }}>Select certificates</Typography>
                    </Stack>
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing={1.5}
                      // The app's one menu-row tone (`menuRowTone.ts`) — see the note
                      // on the row below about this block being a duplicate.
                      sx={(theme: any) => ({
                        cursor: 'pointer',
                        px: 2,
                        py: 1.5,
                        // Absolute px, NOT the tenant token: menu chrome is
                        // deliberately excluded from the brand radius (VRG-562;
                        // see `packages/ui/theme/menuChrome.ts` — "a menu row's
                        // corner would silently track a setting that has nothing
                        // to do with menus").
                        borderRadius: '8px',
                        ...menuRowToneSx(theme),
                      })}
                      onClick={() => {
                        setOpenMore(false);
                        setOpenDisplayedData(true);
                      }}
                    >
                      <LayoutList size={20} />
                      <Typography sx={{ fontSize: 16, ...MENU_ROW_LABEL_SX }}>Displayed data</Typography>
                    </Stack>
                  </Stack>
                }
                handleOpen={uiState.openMore}
                handleClose={() => setOpenMore(false)}
                paperSx={{ height: 160 }}
              />
            <DisplayedDataPopover
              layout={layout}
              onLayoutChange={handleLayoutChange}
              uiState={uiState}
              displayedData={displayedData}
              displayedDataAnchor={displayedDataAnchor}
              handleDisplayedDataChange={handleDisplayedDataChange}
              setOpenDisplayedData={setOpenDisplayedData}
              setDisplayedDataAnchor={setDisplayedDataAnchor}
            />
            {!linkAccounts && (
              <DrawerPopup
                title='Link accounts'
                child={<LinkAccounts handleClose={() => setLinkAccounts(true)} />}
                handleOpen={!linkAccounts}
                handleClose={() => setLinkAccounts(true)}
              />
            )}
            {/* VERA-315: account overflow menu via the shared ActionMenu (was a
                hand-rolled desktop MuiMenu + mobile drawer pair). Transfer history is a
                stub owned by the Transfer epic; Help is real as of VRG-418. */}
            <ActionMenu
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              controlledAnchorEl={anchorEl}
              items={
                [
                  /*
                    ALL BUT TWO DESTINATIONS IN THIS MENU ARE HIDDEN, for two different
                    reasons; the menu holds Help and Log out.

                    STUBS — Transfers, and (until VRG-418) Help center. Both `onClick`
                    into a branch of `performActionBasedOnMenuItem` that `console.log`s a
                    TODO and navigates nowhere, so as shipped they were two of five
                    entries that did nothing when clicked. A menu row that does nothing is
                    a broken promise, and it is a worse one than a missing row: the user
                    concludes the feature is broken rather than absent.

                    Hidden rather than DELETED, deliberately. Deleting would remove
                    the Transfer epic's placeholder from the surface it is meant to
                    land on; `visible: false` keeps the row, its icon, its handler
                    and its TODO exactly where they are, so re-enabling it is one
                    word. Nothing is lost and nothing is promised.

                    ⚠️ VRG-418 — HELP IS NOW REAL, so it is visible again, and that is
                    the whole condition this hide was waiting on: the row was never
                    wrong, its destination did not exist. `/help` does now, so the
                    "broken promise" argument no longer applies to it. Renamed to
                    "Help" — `CertificatesMenu`'s row and Profile's row both say Help,
                    and one destination should not have two names.

                    DUPLICATES — Profile, Notifications. These were `visible:
                    !isMobile` on the reasoning that "they stay here for DESKTOP,
                    where there is no bar". Desktop now has `SideNav`, so that
                    premise is gone: Profile is a rail destination and Notifications
                    lights it (see `components/UI/primaryNav`). Leaving them here
                    would recreate the second, hand-maintained destination list that
                    caused this defect in the first place.

                    ⚠️ FOLLOW-UP, in a file this change could not touch: `HeaderBar`'s
                    hamburger is `display: { xs: 'none', md: 'inline-flex' }`, so at
                    desktop width there is still a Menu button. It used to open a menu
                    holding only Log out — which the rail also offers — and the note here
                    said it should be dropped entirely.

                    That is no longer straightforwardly true: this menu now holds Help,
                    which the desktop rail does NOT offer (Help is deliberately absent
                    from `PRIMARY_NAV_ITEMS`, see `showsPrimaryNav`). Dropping the
                    hamburger today would make Help unreachable from the certificates list
                    at desktop width. Either the rail grows a Help row or the hamburger
                    stays; it is a nav decision, not a cleanup, so it is left standing
                    rather than half-done.
                  */
                  { label: 'Transfers', icon: <ArrowRightLeft size={18} />, onClick: () => handleMenuItemClick('Transfer history'), visible: false },
                  { label: 'Profile', icon: <UserCog size={18} />, onClick: () => handleMenuItemClick('Profile'), visible: false },
                  { label: 'Notifications', icon: <BellIcon size={18} />, onClick: () => handleMenuItemClick('Notifications'), visible: false },
                  { label: 'Help', icon: <CircleHelp size={18} />, onClick: () => handleMenuItemClick('Help'), visible: true },
                  { label: 'Log out', icon: <LogOutIcon size={18} />, onClick: () => handleMenuItemClick('Log out'), divider: true, destructive: true },
                ] as ActionMenuItem[]
              }
            />
            {/* NOT wrapped in `open && (...)`. `handleOpen` below already controls
                it, and the conditional additionally UNMOUNTED the drawer the instant
                the flag went false — so MUI's exit Slide had nothing left to animate
                and the panel vanished rather than sliding away. The wrapper was pure
                redundancy that cost the close transition. Children still unmount on
                close (MUI Modal's `keepMounted` defaults false), so per-open state
                resets exactly as before. */}
              <DrawerPopup
                title='Log out'
                child={<LogOut handleClose={() => setIsLogOutOpen(false)} />}
                handleOpen={isLogOutOpen}
                handleClose={() => setIsLogOutOpen(false)}
              />
            {/* VRG-455 — "Scan to claim ownership".
                ONE sheet for the whole claim: the scan, the outcome, and (on
                the one failure that has a way forward) the checkout-email
                recovery, all as steps of this drawer rather than a stack of
                three (§52). Mounted here rather than inside the card so a card
                unmounting on a background refetch cannot take the open sheet
                down with it. */}
          </Stack>
        </PageContainer>
        <Footer />
      </Stack>
    </FormProvider>
  );
};

export default CerteficatesPage;
